<!--
Copyright (c) 2023-2026, NVIDIA CORPORATION.  All rights reserved.

NVIDIA CORPORATION and its licensors retain all intellectual property
and proprietary rights in and to this software, related documentation
and any modifications thereto.  Any use, reproduction, disclosure or
distribution of this software and related documentation without an express
license agreement from NVIDIA CORPORATION is strictly prohibited.
-->
@if (animationStateMap[sidebarAnimationState].shadow) {
    <div
        [@.disabled]="!expandable"
        [@higSidebarOpacityAnimation]="sidebarAnimationState"
        (@higSidebarOpacityAnimation.done)="onAnimationDone($event)"
        class="hig-sidebar-gradient"
        [attr.role]="expandable && expand ? 'button' : null"
        [attr.aria-label]="expandable && expand ? gradientAriaLabel : null"
        (click)="onSidebarGradientClicked($event)"
    ></div>
}
<div
    #higSidebarContainer
    class="hig-sidebar-container"
    [class.expandable]="expandable"
    [class.show-border]="showBorder"
    [class.enlarge-focused-item]="enlargeFocusedItem"
    [class.collapsing]="sidebarAnimationState === HigSidebarAnimationStateType.Collapsing"
    [class.expand-finished]="sidebarAnimationState === HigSidebarAnimationStateType.Expanded"
    [class]="animationStateMap[sidebarAnimationState].expand && expandable ? 'expanded' : 'collapsed'"
    [dir]="isRtl ? 'rtl' : 'ltr'"
    (click)="onSidebarClicked($event)"
    (focusout)="onFocusOut($event)"
    (focusin)="onFocusIn($event)"
    nvSpatialNavigationGroup
    [attr.tabindex]="spatialNavigationEnabled ? 0 : -1"
    [@.disabled]="!expandable"
    [attr.aria-haspopup]="expandable && !expand ? 'menu' : null"
    [attr.aria-expanded]="expandable && expand ? 'true' : null"
    [attr.aria-label]="sidebarAriaLabel"
    [attr.role]="expandable ? (expand ? 'menu' : 'button') : null"
>
    @if (expandable) {
        <div #higSidebarHighlight class="hig-sidebar-highlight"></div>
    }
    <!-- Account section, will only be shown on expandable sidebar per current design -->
    @if (expandable) {
        <div class="account-info-layout">
            @if (accountInfoSidebarItem) {
                <div class="hig-sidebar-item">
                    <!-- Use "hidden" instead of "*ngIf" on the button when the expandable sidebar is collapsed
          to retain the element in the DOM for focus restoration. -->
                    <button
                        mat-button
                        cdkOverlayOrigin
                        #accountOrigin="cdkOverlayOrigin"
                        (focus)="onItemFocus($event)"
                        (focusin)="onItemFocusIn($event)"
                        (focusout)="onItemFocusOut($event)"
                        (click)="onItemClicked($event, accountInfoSidebarItem.type)"
                        class="account-button"
                        [class.has-badge]="accountInfoSidebarItem?.badgeUrl"
                        [class.selected]="currentSelectedItem?.type === accountInfoSidebarItem.type"
                        [hidden]="!animationStateMap[sidebarAnimationState].expand && hideAccountItemWhenCollapsed"
                        [attr.aria-label]="accountInfoSidebarItem.text | translate"
                        [attr.role]="expandable ? 'menuitem' : null"
                        [attr.aria-hidden]="getAriaHiddenForItem(accountOrigin, hideAccountItemWhenCollapsed)"
                        [attr.aria-current]="currentSelectedItem?.type === accountInfoSidebarItem.type ? 'page' : null"
                        [attr.tabindex]="
                            expandable &&
                            !animationStateMap[sidebarAnimationState].expand &&
                            hideAccountItemWhenCollapsed
                                ? -1
                                : null
                        "
                    >
                        <div class="hig-sidebar-button-content">
                            <mat-icon
                                [svgIcon]="accountInfoSidebarItem.svgIconId"
                                [@higSidebarOpacityAnimation]="sidebarAnimationState"
                            ></mat-icon>
                            @if (accountInfoSidebarItem.badgeUrl && animationStateMap[sidebarAnimationState].icon) {
                                <img
                                    class="avatar-badge"
                                    src="{{ accountInfoSidebarItem.badgeUrl }}"
                                    [@higSidebarOpacityAnimation]="sidebarAnimationState"
                                    alt=""
                                    role="presentation"
                                />
                            }
                            @if (!accountInfoSidebarItem?.loggedIn && animationStateMap[sidebarAnimationState].text) {
                                <span
                                    [@higSidebarTextAnimation]="sidebarAnimationState"
                                    [class]="accountInfoSidebarItem.fontTypeClass ?? 'font-sub2'"
                                >
                                    {{ accountInfoSidebarItem.text | translate }}
                                </span>
                            }
                            @if (accountInfoSidebarItem?.loggedIn && animationStateMap[sidebarAnimationState].text) {
                                <div class="account-info" [@higSidebarTextAnimation]="sidebarAnimationState">
                                    <span class="username font-sub2">{{ accountInfoSidebarItem.text }}</span>
                                    @if (accountInfoSidebarItem.subtext) {
                                        <span class="font-body3">{{ accountInfoSidebarItem.subtext }}</span>
                                    }
                                </div>
                            }
                        </div>
                    </button>
                </div>
            }
        </div>
    }

    <!-- Page Navigation items -->
    @if (pageNavSidebarItems?.length > 0) {
        <div class="page-nav-layout">
            @for (item of pageNavSidebarItems; track item.type) {
                <div class="hig-sidebar-item">
                    <button
                        mat-button
                        cdkOverlayOrigin
                        #pageNavSubMenuOrigin="cdkOverlayOrigin"
                        (focus)="onPageNavItemFocus($event, item)"
                        (focusin)="onItemFocusIn($event)"
                        (focusout)="onItemFocusOut($event, item)"
                        (pointerdown)="onPageNavItemPointerDown($event)"
                        (click)="onPageNavItemClick($event, item)"
                        (keydown)="onPageNavItemKeydown($event, item)"
                        (mouseenter)="onSubMenuTriggerMouseEnter($event, item)"
                        (mouseleave)="onSubMenuTriggerMouseLeave($event)"
                        class="hig-sidebar-item-button"
                        [class.selected]="currentSelectedItem?.type === item.type"
                        [class.sub-menu-open]="isSubMenuOpen(item)"
                        [attr.cdkFocusInitial]="currentSelectedItem?.type === item.type || null"
                        [attr.aria-label]="getNavItemAriaLabel(item)"
                        [attr.aria-haspopup]="hasSubMenuItems(item) ? 'menu' : null"
                        [attr.aria-expanded]="hasSubMenuItems(item) ? (isSubMenuOpen(item) ? 'true' : 'false') : null"
                        [attr.aria-controls]="isSubMenuOpen(item) ? subMenuId : null"
                        [attr.role]="expandable ? 'menuitem' : null"
                        [attr.aria-hidden]="getAriaHiddenForItem(pageNavSubMenuOrigin, true)"
                        [attr.aria-current]="currentSelectedItem?.type === item.type ? 'page' : null"
                        [attr.tabindex]="expandable && !animationStateMap[sidebarAnimationState].expand ? -1 : null"
                    >
                        <div class="hig-sidebar-button-content">
                            @if (item.hasNotificationBadge && !item.hasPageHighlight) {
                                <div class="notification-badge" aria-hidden="true"></div>
                            }
                            @if (item.hasPageHighlight) {
                                <div class="page-highlight-badge" aria-hidden="true"></div>
                            }
                            <mat-icon [svgIcon]="item.svgIconId"></mat-icon>
                            @if (animationStateMap[sidebarAnimationState].text || !expandable) {
                                <span
                                    [@higSidebarTextAnimation]="sidebarAnimationState"
                                    [class]="item.fontTypeClass ?? 'font-sub2'"
                                    class="item-text"
                                    nvEllipsifiedTextWithTooltip
                                    tooltipClass="mat-tooltip-font-body3"
                                    aria-hidden="true"
                                >
                                    {{ item.text | translate }}
                                </span>
                            }
                        </div>
                    </button>
                    @if (showBorderOnSelectedItem) {
                        <div class="button-border" [class.selected]="currentSelectedItem?.type === item.type"></div>
                    }
                    <ng-template
                        cdkConnectedOverlay
                        [cdkConnectedOverlayOrigin]="pageNavSubMenuOrigin"
                        [cdkConnectedOverlayOpen]="isSubMenuOpen(item)"
                        [cdkConnectedOverlayPositions]="subMenuPositions"
                        [cdkConnectedOverlayPanelClass]="subMenuPanelClass"
                        [cdkConnectedOverlayHasBackdrop]="false"
                        [cdkConnectedOverlayPush]="true"
                        [cdkConnectedOverlayViewportMargin]="subMenuViewportMargin"
                        [cdkConnectedOverlayDisableClose]="true"
                        (overlayKeydown)="onSubMenuOverlayKeydown($event)"
                        (overlayOutsideClick)="closeSubMenu()"
                    >
                        <div
                            [id]="subMenuId"
                            class="hig-sidebar-sub-menu"
                            role="menu"
                            [attr.aria-label]="item.text | translate"
                            (mouseenter)="onSubMenuMouseEnter($event)"
                            (mouseleave)="onSubMenuMouseLeave($event)"
                            (focusout)="onSubMenuFocusOut($event)"
                            (keydown)="onSubMenuKeydown($event)"
                        >
                            @if (openSubMenuTitleKey) {
                                <div
                                    class="hig-sidebar-sub-menu-title font-sub3"
                                    role="presentation"
                                    aria-hidden="true"
                                >
                                    <span class="hig-sidebar-sub-menu-title-text">
                                        {{ openSubMenuTitleKey | translate }}
                                    </span>
                                </div>
                            }
                            @for (subMenuItem of item.subMenuItems ?? []; track subMenuItem.id ?? $index) {
                                <button
                                    type="button"
                                    class="hig-sidebar-sub-menu-item font-body3"
                                    role="menuitem"
                                    tabindex="-1"
                                    [attr.aria-label]="subMenuItem.ariaLabel || subMenuItem.text | translate"
                                    (click)="onSubMenuItemClick($event, item, subMenuItem)"
                                >
                                    <span class="hig-sidebar-sub-menu-item-text">
                                        {{ subMenuItem.text | translate }}
                                    </span>
                                </button>
                            }
                        </div>
                    </ng-template>
                </div>
            }
        </div>
    }

    <!-- Modal Navigation items, will only be shown when expanded per current sidebar design -->
    @if (expandable) {
        <div class="modal-nav-layout">
            @for (item of modalNavSidebarItems; track item.type) {
                <div class="hig-sidebar-item">
                    <!-- Use "hidden" instead of "*ngIf" on the button when the expandable sidebar is collapsed
          to retain the element in the DOM for focus restoration. -->
                    <button
                        mat-button
                        cdkOverlayOrigin
                        #modalNavOrigin="cdkOverlayOrigin"
                        (focus)="onItemFocus($event)"
                        (focusin)="onItemFocusIn($event)"
                        (focusout)="onItemFocusOut($event)"
                        (click)="onItemClicked($event, item.type)"
                        [class.selected]="currentSelectedItem?.type === item.type"
                        [hidden]="!animationStateMap[sidebarAnimationState].expand"
                        [attr.aria-label]="item.text | translate"
                        [attr.role]="expandable ? 'menuitem' : null"
                        [attr.aria-hidden]="getAriaHiddenForItem(modalNavOrigin, true)"
                        [attr.aria-current]="currentSelectedItem?.type === item.type ? 'page' : null"
                        [attr.tabindex]="expandable && !animationStateMap[sidebarAnimationState].expand ? -1 : null"
                    >
                        <div class="hig-sidebar-button-content">
                            @if (item.hasNotificationBadge && animationStateMap[sidebarAnimationState].expand) {
                                <div class="notification-badge" aria-hidden="true"></div>
                            }
                            <!--TODO: Add localization for aria-label when we have notification badge-->
                            @if (animationStateMap[sidebarAnimationState].icon) {
                                <mat-icon
                                    [svgIcon]="item.svgIconId"
                                    [@higSidebarOpacityAnimation]="sidebarAnimationState"
                                ></mat-icon>
                            }
                            @if (animationStateMap[sidebarAnimationState].text || !expandable) {
                                <span
                                    [@higSidebarTextAnimation]="sidebarAnimationState"
                                    [class]="item.fontTypeClass ?? 'font-sub2'"
                                >
                                    {{ item.text | translate }}
                                </span>
                            }
                        </div>
                    </button>
                </div>
            }
        </div>
    }

    @if (offlineIcon?.visible) {
        <div class="offline-icon-container">
            <button
                mat-icon-button
                (click)="onOfflineIconClick($event)"
                matTooltip="{{ offlineIcon?.tooltip | translate }}"
                matTooltipClass="mat-tooltip-font-body3"
                [attr.aria-label]="offlineIcon?.tooltip | translate"
            >
                <mat-icon [svgIcon]="offlineIcon?.icon"></mat-icon>
            </button>
        </div>
    }
</div>
